import { useState, useEffect } from "react";
import { icpAPI, billingAPI, apiKeysAPI, authAPI, founderAPI, workspaceAPI, notificationsAPI, monitorAPI } from "../../lib/api";
import { toast } from "sonner";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);

const Toggle = ({ on, onChange, testId }) => (
  <button data-testid={testId} onClick={() => onChange(!on)} style={{
    width: 38, height: 22, borderRadius: 100, border: "none",
    background: on ? ACCENT : BORDER, cursor: "pointer",
    position: "relative", transition: "background 0.2s", flexShrink: 0,
  }}>
    <div style={{
      position: "absolute", top: 3, left: on ? 19 : 3,
      width: 16, height: 16, borderRadius: "50%", background: "#fff",
      transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
    }} />
  </button>
);

const Input = ({ label, value, onChange, placeholder, type = "text", hint }) => (
  <div style={{ marginBottom: 18 }}>
    {label && <label style={{ display: "block", fontSize: 11, fontWeight: 500, color: "#9CA3AF", marginBottom: 6, letterSpacing: "0.03em" }}>{label}</label>}
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{
        width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`,
        borderRadius: 8, padding: "10px 14px", fontSize: 13, color: TEXT_PRIMARY,
        outline: "none", fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.2s",
      }}
      onFocus={e => e.target.style.borderColor = ACCENT}
      onBlur={e => e.target.style.borderColor = BORDER}
    />
    {hint && <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 5 }}>{hint}</p>}
  </div>
);

const Section = ({ title, description, children }) => (
  <div style={{ marginBottom: 32 }}>
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4, fontFamily: "'DM Sans', sans-serif" }}>{title}</h3>
      {description && <p style={{ fontSize: 12, color: TEXT_DIM }}>{description}</p>}
    </div>
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
      {children}
    </div>
  </div>
);

const Row = ({ label, description, children, last }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 16, padding: "14px 18px",
    borderBottom: last ? "none" : `1px solid ${BORDER}`,
  }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY, marginBottom: description ? 3 : 0 }}>{label}</div>
      {description && <div style={{ fontSize: 11, color: TEXT_DIM }}>{description}</div>}
    </div>
    <div style={{ flexShrink: 0 }}>{children}</div>
  </div>
);

const SECTIONS = [
  { id: "profile", label: "Profile", icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" },
  { id: "icp", label: "ICP Profile", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
  { id: "workspace", label: "Workspace", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
  { id: "members", label: "Members + Roles", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
  { id: "billing", label: "Billing + Plan", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
  { id: "notifications", label: "Notifications", icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" },
  { id: "security", label: "Security", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
  { id: "api-keys", label: "API Keys", icon: "M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" },
  { id: "monitoring", label: "Monitoring Cadence", icon: "M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" },
];

const MEMBERS = []; // Was seeded with Alex Johnson / Sarah Kim / Marcus Lee / jessica@acme.com demo team. Now empty; real workspace members flow through the workspace/team API once a user actually invites teammates.

const roleColor = r => ({ Owner: "#F59E0B", Admin: ACCENT, Member: "#22C55E", Viewer: TEXT_MUTED }[r] || TEXT_DIM);

// Look up the caller's workspace from the token payload. We don't have
// a workspace-picker in Settings yet, so use the first workspace the
// user is a member of.
const useWorkspaceId = () => {
  const [ws, setWs] = useState(null);
  useEffect(() => {
    // Every user has an active workspace resolved server-side; the
    // workspace-info endpoint returns it as `active_workspace_id`.
    billingAPI.workspaceInfo()
      .then(r => setWs(r?.data?.active_workspace_id || null))
      .catch(() => setWs(null));
  }, []);
  return ws;
};

/* ── SECTION PANELS ── */

const PERSONA_OPTIONS_SETTINGS = [
  { value: "", label: "Not set" },
  { value: "smb_sales", label: "SMB Sales" },
  { value: "enterprise_ae", label: "Enterprise AE" },
  { value: "pe_bd_corp_dev", label: "PE / BD / Corp Dev" },
  { value: "founder_executive", label: "Founder / Executive" },
];

const ProfileSection = ({ authUser }) => {
  const [name, setName] = useState(authUser?.full_name || "User");
  const [email, setEmail] = useState(authUser?.email || "");
  const [region, setRegion] = useState("United States");
  const [persona, setPersona] = useState(authUser?.onboarding_persona || "");
  const [saved, setSaved] = useState(false);
  const [savingPersona, setSavingPersona] = useState(false);
  
  const initials = authUser?.full_name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";

  const save = async () => {
    // Persona save — fires through the same backend endpoint the onboarding
    // selector uses. Empty string = "Not set" → backend records skip.
    if (persona !== (authUser?.onboarding_persona || "")) {
      setSavingPersona(true);
      try {
        await authAPI.setOnboardingPersona(persona || "skip");
      } catch (e) {
        toast.error("Could not update role preference.");
        setSavingPersona(false);
        return;
      }
      setSavingPersona(false);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div>
      <Section title="Personal Information" description="Your name and contact details.">
        <div style={{ padding: "20px 18px" }}>
          {/* Avatar */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1D4ED8 100%)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, fontWeight: 700, color: "#fff",
            }}>{initials}</div>
            <div>
              <button style={{
                background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7,
                padding: "7px 14px", fontSize: 12, color: TEXT_MUTED, cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}>Change avatar</button>
              <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 5 }}>JPG, PNG up to 2MB</p>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
            <Input label="Full Name" value={name} onChange={e => setName(e.target.value)} />
            <Input label="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} hint="Changing email requires verification." />
          </div>
          <Input label="Default Region" value={region} onChange={e => setRegion(e.target.value)} />

          {/* Role preference — drives the default landing tab for each analysis. */}
          <div style={{ marginBottom: 18 }}>
            <label style={{
              display: "block", fontSize: 12, color: TEXT_MUTED, marginBottom: 6,
              fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em", textTransform: "uppercase",
            }}>
              Role preference
            </label>
            <select
              data-testid="settings-role-preference"
              value={persona}
              onChange={e => setPersona(e.target.value)}
              disabled={savingPersona}
              style={{
                background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7,
                padding: "8px 12px", fontSize: 13, color: TEXT_PRIMARY, outline: "none",
                fontFamily: "'DM Sans', sans-serif", minWidth: 240,
              }}
            >
              {PERSONA_OPTIONS_SETTINGS.map(o => (
                <option key={o.value || "none"} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 6, lineHeight: 1.5 }}>
              Sets the default tab that opens first on every analysis. All tabs remain accessible.
            </p>
          </div>

          <button data-testid="settings-profile-save" onClick={save} disabled={savingPersona} style={{
            background: saved ? "#052E16" : ACCENT,
            color: saved ? "#22C55E" : "#fff",
            border: `1px solid ${saved ? "#14532D" : "transparent"}`,
            borderRadius: 8, padding: "10px 22px", fontSize: 13, fontWeight: 600,
            cursor: savingPersona ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s",
          }}>{savingPersona ? "Saving..." : (saved ? "✓ Saved" : "Save Changes")}</button>
        </div>
      </Section>
    </div>
  );
};

const WorkspaceSection = () => {
  const [wsInfo, setWsInfo] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [creating, setCreating] = useState(false);
  const [activeWs, setActiveWs] = useState(null);
  const [wsName, setWsName] = useState("");
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    billingAPI.workspaceInfo().then(r => setWsInfo(r.data)).catch((e) => {
      console.error("Failed to load workspace billing info:", e);
      toast.error("Could not load account data.");
    });
    // Load the REAL workspace so the name field isn't a placeholder.
    workspaceAPI.list().then(r => {
      const list = r.data || [];
      const storedId = localStorage.getItem("inference_active_workspace_id") || localStorage.getItem("active_workspace_id");
      const active = list.find(w => w.workspace_id === storedId) || list[0] || null;
      setActiveWs(active);
      setWsName(active?.name || "");
    }).catch(() => { /* name field stays empty rather than lying */ });
  }, []);

  const canRename = !!activeWs && ["owner", "admin"].includes((activeWs.role || "owner").toLowerCase());

  const handleRename = async () => {
    const name = wsName.trim();
    if (!activeWs || !name || renaming || name === activeWs.name) return;
    setRenaming(true);
    try {
      await workspaceAPI.rename(activeWs.workspace_id, name);
      setActiveWs(w => ({ ...w, name }));
      toast.success("Workspace renamed", { description: `Now showing as “${name}” across team views and exports.` });
    } catch (err) {
      setWsName(activeWs.name || "");
      toast.error(err?.response?.data?.detail || "Could not rename workspace");
    }
    setRenaming(false);
  };

  const handleAddWorkspace = async () => {
    if (!newWsName.trim() || creating) return;
    if (wsInfo && wsInfo.current_workspaces >= wsInfo.max_workspaces) {
      setShowAddModal(false);
      setShowLimitModal(true);
      return;
    }
    setCreating(true);
    try {
      const res = await billingAPI.subscribeWorkspace(newWsName.trim(), window.location.origin);
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      if (err?.response?.status === 403) {
        setShowAddModal(false);
        setShowLimitModal(true);
      }
    }
    setCreating(false);
  };

  return (
    <div>
      <Section title="Workspace Settings">
        <Row
          label="Workspace Name"
          description={canRename
            ? "Used across team views and exports."
            : "Used across team views and exports. Only owners and admins can change it."}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              data-testid="workspace-name-input"
              value={wsName}
              disabled={!canRename || renaming}
              onChange={e => setWsName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleRename(); }}
              placeholder={activeWs ? "Workspace name" : "Loading…"}
              style={{
                background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7,
                padding: "8px 12px", fontSize: 13, color: TEXT_PRIMARY, outline: "none",
                fontFamily: "'DM Sans', sans-serif", width: 200,
                opacity: canRename ? 1 : 0.6,
              }}
              onFocus={e => e.target.style.borderColor = ACCENT}
              onBlur={e => e.target.style.borderColor = BORDER}
            />
            {canRename && wsName.trim() && wsName.trim() !== (activeWs?.name || "") && (
              <button data-testid="workspace-rename-save-btn" onClick={handleRename} disabled={renaming} style={{
                background: ACCENT, color: "#fff", border: "none", borderRadius: 7,
                padding: "8px 14px", fontSize: 12, fontWeight: 600,
                cursor: renaming ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
              }}>{renaming ? "Saving..." : "Save"}</button>
            )}
          </div>
        </Row>
        {wsInfo && wsInfo.max_workspaces > 1 && (
          <Row label="Multi-Workspace" description={`${wsInfo.current_workspaces} of ${wsInfo.max_workspaces} workspaces in use. Each workspace is fully isolated.`} last>
            <button data-testid="add-workspace-btn" onClick={() => {
              if (wsInfo.can_add) setShowAddModal(true);
              else setShowLimitModal(true);
            }} style={{
              background: wsInfo.can_add ? `${ACCENT}14` : SURFACE_2,
              border: `1px solid ${wsInfo.can_add ? ACCENT + "44" : BORDER}`,
              borderRadius: 7, padding: "7px 14px", fontSize: 12,
              color: wsInfo.can_add ? ACCENT : TEXT_DIM, cursor: "pointer",
            }}>{wsInfo.can_add ? "+ Add Workspace" : `${wsInfo.current_workspaces}/${wsInfo.max_workspaces} Workspaces`}</button>
          </Row>
        )}
        {(!wsInfo || wsInfo.max_workspaces <= 1) && (
          <Row label="Personal Workspace" description="This is your personal workspace. You have one workspace per account." last>
            <Badge label="PERSONAL" color={TEXT_MUTED} />
          </Row>
        )}
      </Section>

      {/* Existing workspaces list */}
      {wsInfo && wsInfo.workspaces && wsInfo.workspaces.length > 0 && (
        <Section title="Your Workspaces" description={`Each workspace has isolated data, seats, and billing.`}>
          {wsInfo.workspaces.map((ws, i) => (
            <Row key={ws.workspace_id} label={ws.name || `Workspace ${i + 1}`}
              description={`${ws.members} members · ${ws.analyses} analyses · ${ws.monitors} monitors`}
              last={i === wsInfo.workspaces.length - 1}>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{ws.workspace_id}</span>
            </Row>
          ))}
        </Section>
      )}

      <Section title="Danger Zone" description="Irreversible actions. Proceed with caution.">
        <Row label="Export all data" description="Download all your analyses, notes, and outputs as JSON.">
          <button style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 7, padding: "7px 14px", fontSize: 12, color: TEXT_MUTED, cursor: "pointer" }}>Export</button>
        </Row>
        <Row label="Delete workspace" description="Permanently deletes all data. This cannot be undone." last>
          <button style={{ background: "#200A0A", border: "1px solid #7F1D1D", borderRadius: 7, padding: "7px 14px", fontSize: 12, color: "#EF4444", cursor: "pointer" }}>Delete workspace</button>
        </Row>
      </Section>

      {/* Add Workspace Modal */}
      {showAddModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div data-testid="add-workspace-modal" style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "28px 32px", width: 420 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 6 }}>Add New Workspace</h2>
            <p style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 16 }}>
              Workspace #{(wsInfo?.current_workspaces || 0) + 1} — ${(wsInfo?.additional_price || 0).toLocaleString()}/year
              {(wsInfo?.current_workspaces || 0) > 0 && <span style={{ color: "#22C55E" }}> ({wsInfo?.discount_pct}% multi-workspace discount applied)</span>}
            </p>
            <input data-testid="new-workspace-name" value={newWsName} onChange={e => setNewWsName(e.target.value)}
              placeholder="Workspace name (e.g., Portfolio Fund II)"
              style={{ width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: TEXT_PRIMARY, outline: "none", marginBottom: 16, fontFamily: "'DM Sans', sans-serif" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: SURFACE_2, color: TEXT_MUTED, border: `1px solid ${BORDER}`, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button data-testid="confirm-add-workspace" onClick={handleAddWorkspace} disabled={!newWsName.trim() || creating}
                style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: ACCENT, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: newWsName.trim() ? 1 : 0.4 }}>
                {creating ? "Processing..." : "Subscribe & Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workspace Limit Modal */}
      {showLimitModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setShowLimitModal(false); }}>
          <div data-testid="workspace-limit-modal" style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "28px 32px", width: 480 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 8 }}>Workspace Limit Reached</h2>
            <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 20 }}>
              You are running {wsInfo?.current_workspaces || 3} workspaces. Adding a fourth requires an <span style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>Enterprise plan</span> which gives you unlimited workspaces, unlimited seats, daily monitoring, and dedicated support. Contact us to upgrade.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowLimitModal(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: SURFACE_2, color: TEXT_MUTED, border: `1px solid ${BORDER}`, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
              <a href="/contact" data-testid="book-demo-btn" style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: ACCENT, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "center", textDecoration: "none", display: "block" }}>Book a Demo</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MembersSection = () => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const workspaceId = useWorkspaceId();

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    workspaceAPI.members(workspaceId)
      .then(r => { if (!cancelled) { setMembers(r?.data || []); setLoading(false); } })
      .catch(e => {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || "Could not load team members");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const initials = (nameOrEmail) => {
    if (!nameOrEmail) return "?";
    if (nameOrEmail.includes("@")) return "?";
    return nameOrEmail.split(/\s+/).filter(Boolean).slice(0, 2).map(n => n[0]).join("").toUpperCase() || "?";
  };

  return (
    <div>
      <Section title="Team Members">
        {loading ? (
          <div data-testid="members-loading" style={{ padding: "20px 18px", fontSize: 13, color: TEXT_MUTED }}>Loading team members…</div>
        ) : members.length === 0 ? (
          <div data-testid="members-empty" style={{ padding: "22px 18px" }}>
            <p style={{ fontSize: 13, color: TEXT_PRIMARY, marginBottom: 4 }}>No team members yet.</p>
            <p style={{ fontSize: 12, color: TEXT_MUTED }}>Invite a colleague below to share this workspace.</p>
          </div>
        ) : members.map((m, i) => (
          <div key={m.user_id || m.email || i} data-testid={`member-row-${i}`} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", borderBottom: i < members.length - 1 ? `1px solid ${BORDER}` : "none" }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
              background: `${ACCENT}22`, border: `1px solid ${ACCENT}44`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, color: ACCENT,
            }}>
              {initials(m.name)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>{m.name}</span>
                {m.you && <Badge label="YOU" color={TEXT_DIM} />}
                <Badge label={(m.status || "Active").toUpperCase()} color={m.status === "Active" ? "#22C55E" : "#F59E0B"} />
              </div>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{m.email}</span>
            </div>
            <Badge label={(m.role || "Member").toUpperCase()} color={roleColor(m.role)} />
            {!m.you && (
              <button style={{ background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 6, padding: "5px 10px", fontSize: 11, color: TEXT_DIM, cursor: "pointer" }}>
                {m.status === "Invited" ? "Revoke" : "Remove"}
              </button>
            )}
          </div>
        ))}
      </Section>
      <Section title="Invite Member" description="Members get access to the shared workspace based on their role.">
        <div style={{ padding: "16px 18px", display: "flex", gap: 10 }}>
          <input
            placeholder="colleague@company.com"
            value={inviteEmail}
            data-testid="invite-email-input"
            onChange={e => setInviteEmail(e.target.value)}
            style={{
              flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
              padding: "10px 14px", fontSize: 13, color: TEXT_PRIMARY, outline: "none", fontFamily: "'DM Sans', sans-serif",
            }}
            onFocus={e => e.target.style.borderColor = ACCENT}
            onBlur={e => e.target.style.borderColor = BORDER}
          />
          <select data-testid="invite-role-select" style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: TEXT_MUTED, outline: "none" }}>
            <option>Member</option><option>Viewer</option><option>Admin</option>
          </select>
          <button
            data-testid="send-invite-btn"
            disabled={!inviteEmail}
            onClick={() => toast.info("Invitations are coming soon — for now share the invite code with your team.")}
            style={{
              background: inviteEmail ? ACCENT : "#1E3A6E", color: inviteEmail ? "#fff" : "#6B9AE0",
              border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600,
              cursor: inviteEmail ? "pointer" : "not-allowed", fontFamily: "'DM Sans', sans-serif",
            }}
          >Send Invite</button>
        </div>
      </Section>
    </div>
  );
};

const _formatDateLong = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch (e) {
    return iso;
  }
};

const BillingSection = () => {
  const [tierInfo, setTierInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  // Founder Tools — visible only when the backend `/api/founder/me` probe
  // returns 200 (i.e. caller's email is in FOUNDER_EMAILS or the DB flag is
  // already stamped). Non-founders silently see nothing.
  const [founderEligible, setFounderEligible] = useState(false);
  const [founderIsStamped, setFounderIsStamped] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState(null);

  const isPaid = tierInfo && tierInfo.tier_id && tierInfo.tier_id !== "free";
  const alreadyCancelled = !!cancelResult || !!(tierInfo && tierInfo.cancellation_effective_date);
  const effectiveDate = cancelResult?.cancellation_effective_date || tierInfo?.cancellation_effective_date;

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tierRes, usageRes, founderProbe] = await Promise.all([
        billingAPI.tier(),
        billingAPI.usage().catch(() => ({ data: null })),
        founderAPI.me().catch(() => null),
      ]);
      setTierInfo(tierRes.data);
      setUsage(usageRes?.data || null);
      if (founderProbe?.data?.eligible) {
        setFounderEligible(true);
        setFounderIsStamped(!!founderProbe.data.is_founder);
      } else {
        setFounderEligible(false);
        setFounderIsStamped(false);
      }
    } catch (err) {
      console.error("Failed to load billing:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await billingAPI.cancelSubscription();
      setCancelResult(res.data);
      setConfirmOpen(false);
      toast.success(
        `Your subscription has been cancelled. You will retain access until ${_formatDateLong(res.data.cancellation_effective_date)}.`
      );
      // Refresh tier card so it reflects the cancellation state.
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to cancel subscription. Please try again.";
      toast.error(detail);
    } finally {
      setCancelling(false);
    }
  };

  const handleSelfPromote = async () => {
    setPromoting(true);
    try {
      const res = await founderAPI.selfPromote();
      setPromoteResult(res.data);
      setFounderIsStamped(true);
      toast.success("Founder upgrade applied — tier_4_plus active on your workspace.");
      // Refresh tier card so it shows the new tier immediately.
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to apply founder upgrade.";
      toast.error(detail);
    } finally {
      setPromoting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="billing-loading" style={{ padding: 24, color: TEXT_MUTED, fontSize: 13 }}>
        Loading billing details…
      </div>
    );
  }

  const tierName = tierInfo?.name || tierInfo?.tier_id || "Free";
  const priceMo = Number(tierInfo?.price_monthly || 0);
  const seats = tierInfo?.seats || 1;
  const cap = tierInfo?.manual_cap || 0;
  const subtitle = `${priceMo > 0 ? `$${priceMo} / month` : "Free"} · ${seats} seat${seats === 1 ? "" : "s"} · ${cap} analyses/month`;
  const used = usage?.used ?? 0;
  const totalCap = usage?.total_cap ?? cap;

  return (
    <div data-testid="billing-section">
      {/* Founder Tools — only visible to emails in FOUNDER_EMAILS. Used to
          self-heal a founder account that was created before the SSO
          callbacks honoured the founder gate, leaving them stuck on free. */}
      {founderEligible && (
        <div
          data-testid="founder-tools-card"
          style={{
            background: `linear-gradient(135deg, #1A1408 0%, #0D0907 100%)`,
            border: `1px solid #F59E0B40`, borderRadius: 10,
            padding: "18px 22px", marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: "#F59E0B", fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em" }}>FOUNDER TOOLS</span>
            <Badge label="ADMIN" color="#F59E0B" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>
            Self-promote to Tier 4 Org Plus
          </div>
          <div style={{ fontSize: 12.5, color: TEXT_MUTED, lineHeight: 1.55, marginBottom: 14 }}>
            One-click idempotent fix for founder accounts created before the SSO callbacks honoured the founder gate.
            Stamps <code style={{ color: "#F59E0B" }}>is_founder: true</code> on your user document and upgrades every workspace you own to <code style={{ color: "#F59E0B" }}>tier_4_plus</code> with full quotas (1,250 analyses/mo, 25 seats, 2,500 monitors, 50 batch cap, 25 API keys).
            Safe to run multiple times.
          </div>
          {promoteResult && (
            <div
              data-testid="founder-promote-result"
              style={{
                background: "#0D2818", border: "1px solid #22C55E40",
                borderRadius: 7, padding: "10px 12px", marginBottom: 12,
                fontSize: 12.5, color: "#86EFAC",
              }}
            >
              ✓ Upgraded {promoteResult.users_modified} user{promoteResult.users_modified === 1 ? "" : "s"} and {promoteResult.workspaces_modified} workspace{promoteResult.workspaces_modified === 1 ? "" : "s"} to tier_4_plus.
              {promoteResult.workspaces?.length > 0 && (
                <div style={{ marginTop: 6, color: TEXT_MUTED }}>
                  {promoteResult.workspaces.map(w => (
                    <div key={w.workspace_id}>
                      • {w.name || w.workspace_id} → tier: <strong style={{ color: "#86EFAC" }}>{w.tier}</strong>, quota: {w.analysis_quota}, seats: {w.members_limit}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            data-testid="founder-self-promote-btn"
            onClick={handleSelfPromote}
            disabled={promoting}
            style={{
              background: promoting ? "#78350F" : "#F59E0B", color: "#1A1408",
              border: "none", borderRadius: 8, padding: "9px 18px",
              fontSize: 13, fontWeight: 700, cursor: promoting ? "not-allowed" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {promoting ? "Applying…" : founderIsStamped ? "Re-run upgrade (idempotent)" : "Apply founder upgrade"}
          </button>
        </div>
      )}

      {/* Current plan */}
      <div style={{
        background: `linear-gradient(135deg, #0D1829 0%, #090B12 100%)`,
        border: `1px solid ${ACCENT}33`, borderRadius: 10, padding: "20px 22px", marginBottom: 20,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: ACCENT, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", marginBottom: 6 }}>CURRENT PLAN</div>
            <div data-testid="billing-tier-name" style={{ fontSize: 20, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 4, fontFamily: "'DM Sans', sans-serif" }}>{tierName}</div>
            <div style={{ fontSize: 13, color: TEXT_MUTED }}>{subtitle}</div>
            {alreadyCancelled && (
              <div
                data-testid="billing-cancellation-notice"
                style={{
                  marginTop: 12, padding: "8px 12px", borderRadius: 7,
                  background: "#F59E0B14", border: "1px solid #F59E0B40",
                  color: "#F59E0B", fontSize: 12.5, fontWeight: 500,
                }}
              >
                Cancellation scheduled. Access until {_formatDateLong(effectiveDate)} — then reverts to Free.
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
            {!isPaid && (
              <button data-testid="billing-upgrade-btn" style={{
                background: ACCENT, color: "#fff", border: "none", borderRadius: 8,
                padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              }}>Upgrade Plan</button>
            )}
            {isPaid && !alreadyCancelled && !founderIsStamped && (
              <button
                data-testid="billing-cancel-btn"
                onClick={() => setConfirmOpen(true)}
                style={{
                  background: "transparent", color: "#F87171",
                  border: "1px solid #F8717155", borderRadius: 8,
                  padding: "10px 18px", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                }}
              >Cancel Subscription</button>
            )}
          </div>
        </div>
        {usage && (
          <div style={{ marginTop: 16, display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 3 }}>ANALYSES USED</div>
              <div style={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 500 }} data-testid="billing-usage-analyses">{used} / {totalCap}</div>
            </div>
            {usage?.next_reset && (
              <div>
                <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 3 }}>NEXT RESET</div>
                <div style={{ fontSize: 13, color: TEXT_PRIMARY, fontWeight: 500 }}>{_formatDateLong(usage.next_reset)}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      {confirmOpen && (
        <div
          data-testid="billing-cancel-dialog"
          style={{
            position: "fixed", inset: 0, background: "rgba(6,8,15,0.78)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 24,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 12,
              maxWidth: 460, width: "100%", padding: "28px 28px 22px",
              boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{
              fontSize: 11, color: "#F87171", fontFamily: "'DM Mono', monospace",
              letterSpacing: "0.08em", marginBottom: 12,
            }}>CONFIRM CANCELLATION</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
              Cancel your {tierName} subscription?
            </h3>
            <p style={{ fontSize: 13.5, color: TEXT_MUTED, lineHeight: 1.65, margin: 0 }}>
              Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period. After that your account will revert to the Free tier.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
              <button
                data-testid="billing-cancel-dialog-keep"
                onClick={() => setConfirmOpen(false)}
                disabled={cancelling}
                style={{
                  background: "transparent", color: TEXT_PRIMARY,
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  padding: "9px 18px", fontSize: 13, fontWeight: 600,
                  cursor: cancelling ? "not-allowed" : "pointer", fontFamily: "'DM Sans', sans-serif",
                }}
              >Keep My Plan</button>
              <button
                data-testid="billing-cancel-dialog-confirm"
                onClick={handleCancel}
                disabled={cancelling}
                style={{
                  background: cancelling ? "#7F1D1D" : "#DC2626", color: "#fff", border: "none", borderRadius: 8,
                  padding: "9px 18px", fontSize: 13, fontWeight: 600,
                  cursor: cancelling ? "not-allowed" : "pointer", fontFamily: "'DM Sans', sans-serif",
                }}
              >{cancelling ? "Cancelling…" : "Cancel Subscription"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Plan comparison */}
      <Section title="Plans">
        {[
          { name: "Individual", price: "Free", features: ["20 analyses/mo", "1 seat", "Basic exports"], current: !isPaid },
          { name: "Team", price: "$49/mo", features: ["Unlimited analyses", "Up to 10 seats", "Shared folders", "Decision notes", "Team activity"], current: false },
          { name: "Org", price: "$199/mo", features: ["Everything in Team", "Monitoring cadence control", "Batch processing", "Audit trail", "SSO + SCIM", "RBAC editor"], current: false },
        ].map((p, i) => (
          <div key={i} style={{
            padding: "16px 18px", borderBottom: i < 2 ? `1px solid ${BORDER}` : "none",
            display: "flex", alignItems: "center", gap: 16,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY }}>{p.name}</span>
                {p.current && <Badge label="CURRENT" color="#22C55E" />}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {p.features.map(f => (
                  <div key={f} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth={2.5}><polyline points="20 6 9 17 4 12" /></svg>
                    <span style={{ fontSize: 11, color: TEXT_MUTED }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 8 }}>{p.price}</div>
              {!p.current && (
                <button style={{
                  background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 7,
                  padding: "7px 16px", fontSize: 12, color: ACCENT, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                }}>Upgrade →</button>
              )}
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
};

const NotificationsSection = () => {
  const [prefs, setPrefs] = useState({
    material_changes: true, minor_changes: false,
    scan_completed: true, fit_changed: false,
    new_articles: true, team_activity: false,
    email_alerts: true, in_app: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notificationsAPI.getPrefs()
      .then(res => { if (res.data) setPrefs(res.data); })
      .catch(() => { /* fall back to defaults on screen */ })
      .finally(() => setLoading(false));
  }, []);

  // Each toggle persists immediately — the previous build kept these in
  // local state only, so preferences vanished on reload.
  const set = k => async v => {
    const prev = prefs;
    setPrefs(p => ({ ...p, [k]: v }));
    try {
      const res = await notificationsAPI.savePrefs({ [k]: v });
      if (res.data) setPrefs(res.data);
    } catch (err) {
      setPrefs(prev);
      toast.error("Could not save that preference");
    }
  };

  if (loading) return <p style={{ color: TEXT_DIM, fontSize: 13 }}>Loading notification preferences...</p>;

  return (
    <Section title="Notification Preferences" description="Control what triggers a notification and how you receive them.">
      {[
        { key: "material_changes", label: "Material changes detected", desc: "When a monitored company has a material delta" },
        { key: "minor_changes", label: "Minor changes detected", desc: "When a monitored company has a minor delta" },
        { key: "scan_completed", label: "Scan completed", desc: "When an analysis finishes" },
        { key: "fit_changed", label: "Fit score changed", desc: "When fit score changes after re-scan" },
        { key: "new_articles", label: "New articles discovered", desc: "New relevant articles for monitored companies" },
        { key: "team_activity", label: "Team activity", desc: "When team members complete analyses or add notes" },
        { key: null }, // divider
        { key: "email_alerts", label: "Email alerts", desc: "Receive notifications by email" },
        { key: "in_app", label: "In-app notifications", desc: "Show notifications in the notification bell" },
      ].map((item, i, arr) => {
        if (!item.key) return <div key={i} style={{ height: 1, background: BORDER, margin: "4px 0" }} />;
        return (
          <Row key={item.key} label={item.label} description={item.desc} last={i === arr.length - 1}>
            <Toggle on={prefs[item.key]} onChange={set(item.key)} testId={`notif-toggle-${item.key}`} />
          </Row>
        );
      })}
    </Section>
  );
};

const SecuritySection = () => {
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const r = await authAPI.listSessions();
      setSessions(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      console.error("Failed to load sessions:", err);
      toast.error("Could not load account data.");
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => { loadSessions(); }, []);

  const handleRevoke = async (s) => {
    if (!s?.session_id || revokingId) return;
    setRevokingId(s.session_id);
    const prev = sessions;
    setSessions(prev.filter(x => x.session_id !== s.session_id));
    try {
      const res = await authAPI.revokeSession(s.session_id);
      toast.success("Session signed out.");
      if (res.data?.is_current) {
        toast("Signing you out…");
        setTimeout(() => {
          try { localStorage.clear(); } catch (e) { /* ignore */ }
          window.location.assign("/login");
        }, 800);
      }
    } catch (err) {
      console.error("Revoke failed:", err);
      toast.error("Could not sign out that session.");
      setSessions(prev);
    } finally {
      setRevokingId(null);
    }
  };

  const formatRelative = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch (e) { return "—"; }
  };

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const handleUpdatePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) {
      toast.error("Please fill in all password fields.");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSavingPwd(true);
    try {
      await authAPI.changePassword(currentPwd, newPwd);
      toast.success("Password updated.");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err) {
      console.error("Change password failed:", err);
      toast.error("Could not update password. Please check your current password.");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div>
      <Section title="Password" description="Update your password. We recommend using a strong, unique password.">
        <div style={{ padding: "16px 18px" }}>
          <Input label="Current password" type="password" placeholder="••••••••" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} />
          <Input label="New password" type="password" placeholder="Min. 8 characters" value={newPwd} onChange={e => setNewPwd(e.target.value)} />
          <Input label="Confirm new password" type="password" placeholder="••••••••" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} />
          <button
            data-testid="update-password-btn"
            onClick={handleUpdatePassword}
            disabled={savingPwd}
            style={{
            background: ACCENT, color: "#fff", border: "none", borderRadius: 8,
            padding: "10px 20px", fontSize: 13, fontWeight: 600,
            cursor: savingPwd ? "wait" : "pointer", opacity: savingPwd ? 0.7 : 1,
            fontFamily: "'DM Sans', sans-serif",
          }}>{savingPwd ? "Updating…" : "Update Password"}</button>
        </div>
      </Section>

      <Section title="Multi-Factor Authentication" description="MFA fields are schema-ready. Activation available on Team/Org plans.">
        <Row label="Authenticator App" description="Use an authenticator app (TOTP)" last>
          <span
            data-testid="mfa-coming-soon"
            title="MFA configuration is coming soon"
            style={{
              fontSize: 11, color: TEXT_DIM, background: SURFACE_2,
              border: `1px dashed ${BORDER}`, borderRadius: 7,
              padding: "6px 12px", fontFamily: "'DM Mono', monospace",
              letterSpacing: "0.06em",
            }}>COMING SOON</span>
        </Row>
      </Section>

      <Section title="Active Sessions" description="Devices currently signed in. Revoke any you don't recognise.">
        {sessionsLoading ? (
          <div data-testid="sessions-loading" style={{ padding: "24px 18px", display: "flex", justifyContent: "center" }}>
            <span style={{ width: 18, height: 18, border: `2px solid ${ACCENT}33`, borderTopColor: ACCENT, borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
          </div>
        ) : sessions.length === 0 ? (
          <div data-testid="sessions-empty-state" style={{ padding: "18px", fontSize: 12, color: TEXT_MUTED }}>
            No active sessions found.
          </div>
        ) : (
          sessions.map((s, i) => (
            <div
              key={s.session_id || i}
              data-testid={`session-row-${s.session_id}`}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "12px 18px",
                borderBottom: i < sessions.length - 1 ? `1px solid ${BORDER}` : "none",
              }}
            >
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: s.is_current ? "#22C55E" : TEXT_DIM, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {s.device || "Unknown device"}
                  {s.auth_method === "google" && (
                    <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "1px 6px" }}>GOOGLE</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
                  {s.ip || "—"} · last active {formatRelative(s.last_active_at)}
                </div>
              </div>
              {s.is_current ? (
                <Badge label="THIS SESSION" color="#22C55E" />
              ) : (
                <button
                  data-testid={`session-revoke-${s.session_id}`}
                  onClick={() => handleRevoke(s)}
                  disabled={revokingId === s.session_id}
                  style={{
                    background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 6,
                    padding: "5px 10px", fontSize: 11, color: TEXT_DIM,
                    cursor: revokingId === s.session_id ? "wait" : "pointer",
                    opacity: revokingId === s.session_id ? 0.6 : 1,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >{revokingId === s.session_id ? "Signing out…" : "Sign out"}</button>
              )}
            </div>
          ))
        )}
      </Section>
    </div>
  );
};

const TIER4_TIERS = new Set(["tier_4_std", "tier_4_plus", "enterprise"]);

const APIKeysSection = ({ onUpgrade }) => {
  const [tierId, setTierId] = useState(null);
  const [tierConfig, setTierConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState([]);
  const [maxKeys, setMaxKeys] = useState(0);
  const [workspaceId, setWorkspaceId] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // {name, raw, masked}
  const [error, setError] = useState(null);

  const loadTier = async () => {
    try {
      const r = await billingAPI.tier();
      setTierId(r.data.tier_id);
      setTierConfig(r.data);
    } catch (e) {
      console.error("Failed to load tier:", e);
      toast.error("Could not load account data.");
      setTierId("free");
    }
  };

  const loadKeys = async () => {
    try {
      const r = await apiKeysAPI.list();
      setKeys(r.data.keys || []);
      setMaxKeys(r.data.max_keys || 0);
      setWorkspaceId(r.data.workspace_id || "");
      setError(null);
    } catch (e) {
      // 403 means tier doesn't allow — surface gracefully
      if (e?.response?.status !== 403) {
        console.error("Failed to load API keys:", e);
        toast.error("Could not load account data.");
        setError(e?.response?.data?.detail || "Failed to load API keys");
      }
    }
  };

  useEffect(() => {
    (async () => {
      await loadTier();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (tierId && TIER4_TIERS.has(tierId)) {
      loadKeys();
    }
  }, [tierId]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await apiKeysAPI.create(newName.trim(), ["analyze", "read"]);
      setRevealedKey({ name: r.data.name, raw: r.data.api_key, masked: r.data.masked_key });
      setNewName("");
      setShowCreate(false);
      await loadKeys();
      toast.success("API key created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create key");
    }
    setCreating(false);
  };

  // ESC key handler — closes the inline Create New Key form (and the reveal modal if open).
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== "Escape") return;
      if (revealedKey) setRevealedKey(null);
      else if (showCreate) { setShowCreate(false); setNewName(""); }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [showCreate, revealedKey]);

  const handleRevoke = async (keyId) => {
    if (!window.confirm("Revoke this API key? Any Salesforce or external integration using it will stop working immediately.")) return;
    try {
      await apiKeysAPI.revoke(keyId);
      await loadKeys();
      toast.success("API key revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  if (loading) {
    return (
      <Section title="API Keys">
        <div style={{ padding: "24px 18px", fontSize: 12, color: TEXT_DIM }}>Loading…</div>
      </Section>
    );
  }

  // Tier gate — show upgrade CTA for non-Tier 4
  if (!TIER4_TIERS.has(tierId)) {
    return (
      <div data-testid="api-keys-upgrade-prompt">
        <Section title="API Access" description="Connect Inference to Salesforce, Snowflake, or any custom workflow.">
          <div style={{ padding: "28px 24px", textAlign: "center" }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, background: `${ACCENT}18`,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginBottom: 14,
            }}>
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h4 style={{ fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 8 }}>
              Unlock API access
            </h4>
            <p style={{ fontSize: 13, color: TEXT_MUTED, maxWidth: 460, margin: "0 auto 18px", lineHeight: 1.6 }}>
              API access is available on <strong style={{ color: TEXT_PRIMARY }}>Org Standard</strong>, <strong style={{ color: TEXT_PRIMARY }}>Org Plus</strong>, and <strong style={{ color: TEXT_PRIMARY }}>Enterprise</strong> plans. Upgrade to connect Inference to Salesforce and other tools.
            </p>
            <button
              data-testid="api-keys-upgrade-btn"
              onClick={() => onUpgrade && onUpgrade()}
              style={{
                background: ACCENT, color: "#fff", border: "none",
                padding: "10px 22px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Upgrade Plan
            </button>
            <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${BORDER}` }}>
              <a href="/docs/salesforce" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: "none" }}>
                Read the Salesforce Quickstart →
              </a>
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="api-keys-section">
      {/* Reveal-once modal */}
      {revealedKey && (
        <div
          data-testid="api-key-reveal-modal"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000, padding: 20,
          }}
          onClick={() => setRevealedKey(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 12,
              padding: 26, maxWidth: 540, width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22C55E" }} />
              <h3 style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY }}>API key created</h3>
            </div>
            <p style={{ fontSize: 12.5, color: TEXT_MUTED, marginBottom: 16, lineHeight: 1.6 }}>
              <strong style={{ color: "#FCD34D" }}>Save this key now.</strong> For security, the full key will not be shown again. You can rotate or revoke it anytime.
            </p>
            <div style={{
              background: "#05070C", border: `1px solid ${BORDER}`, borderRadius: 8,
              padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
              marginBottom: 16,
            }}>
              <code data-testid="revealed-api-key" style={{ flex: 1, fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#93C5FD", wordBreak: "break-all" }}>
                {revealedKey.raw}
              </code>
              <button
                data-testid="copy-api-key-btn"
                onClick={() => copyToClipboard(revealedKey.raw)}
                style={{
                  background: ACCENT, color: "#fff", border: "none",
                  padding: "6px 12px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                  cursor: "pointer", flexShrink: 0,
                }}
              >
                Copy
              </button>
            </div>
            <button
              data-testid="close-reveal-modal"
              onClick={() => setRevealedKey(null)}
              style={{
                background: "transparent", border: `1px solid ${BORDER}`,
                color: TEXT_MUTED, padding: "8px 16px", borderRadius: 6,
                fontSize: 12, fontWeight: 500, cursor: "pointer",
              }}
            >
              I've saved the key
            </button>
          </div>
        </div>
      )}

      <Section
        title="API Keys"
        description={`Workspace-scoped keys for /api/v1/analyze. Each key is bound to this workspace only.`}
      >
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 2 }}>
              <span data-testid="api-keys-usage" style={{ color: TEXT_PRIMARY, fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>
                {keys.length} / {maxKeys}
              </span> keys used
            </div>
            <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
              workspace: {workspaceId || "—"}
            </div>
          </div>
          <button
            data-testid="create-api-key-btn"
            onClick={() => setShowCreate(true)}
            disabled={keys.length >= maxKeys}
            style={{
              background: keys.length >= maxKeys ? SURFACE_2 : ACCENT,
              color: keys.length >= maxKeys ? TEXT_DIM : "#fff",
              border: "none", padding: "8px 16px", borderRadius: 6,
              fontSize: 12, fontWeight: 600,
              cursor: keys.length >= maxKeys ? "not-allowed" : "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            + Create New Key
          </button>
        </div>

        {showCreate && (
          <div data-testid="create-api-key-form" style={{ padding: "16px 44px 16px 18px", borderBottom: `1px solid ${BORDER}`, background: `${ACCENT}06`, position: "relative" }}>
            <button
              data-testid="create-api-key-form-close"
              onClick={() => { setShowCreate(false); setNewName(""); }}
              aria-label="Close"
              style={{
                position: "absolute", top: 8, right: 8,
                background: "transparent", color: TEXT_MUTED, border: "none", cursor: "pointer",
                padding: 4, display: "flex", alignItems: "center", borderRadius: 4,
                transition: "color 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.color = TEXT_PRIMARY; }}
              onMouseLeave={e => { e.currentTarget.style.color = TEXT_MUTED; }}
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. salesforce-prod"
                data-testid="new-key-name-input"
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                style={{
                  flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`,
                  borderRadius: 6, padding: "8px 12px", fontSize: 12.5, color: TEXT_PRIMARY,
                  outline: "none", fontFamily: "'DM Sans', sans-serif",
                }}
              />
              <button
                data-testid="confirm-create-key-btn"
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                style={{
                  background: ACCENT, color: "#fff", border: "none",
                  padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: creating ? "wait" : "pointer", opacity: !newName.trim() ? 0.5 : 1,
                }}
              >
                {creating ? "Creating…" : "Create"}
              </button>
              <button
                data-testid="cancel-create-key-btn"
                onClick={() => { setShowCreate(false); setNewName(""); }}
                style={{
                  background: "transparent", color: TEXT_MUTED, border: `1px solid ${BORDER}`,
                  padding: "8px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && (
          <div style={{ padding: "14px 18px", color: "#FCA5A5", fontSize: 12.5, borderBottom: `1px solid ${BORDER}` }}>
            {error}
          </div>
        )}

        {keys.length === 0 && !showCreate && (
          <div data-testid="no-api-keys" style={{ padding: "32px 18px", textAlign: "center", fontSize: 12.5, color: TEXT_DIM }}>
            No API keys yet. Create one to start integrating with Salesforce.
          </div>
        )}

        {keys.map((k, i) => (
          <div
            key={k.key_id}
            data-testid={`api-key-row-${k.key_id}`}
            style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 18px",
              borderBottom: i < keys.length - 1 ? `1px solid ${BORDER}` : "none",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>{k.name}</span>
                {(k.scopes || []).map((s) => (
                  <Badge key={s} label={s} />
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
                {k.masked_key} · created {new Date(k.created_at).toLocaleDateString()}{" "}
                {k.last_used ? `· last used ${new Date(k.last_used).toLocaleDateString()}` : "· never used"}
              </div>
            </div>
            <button
              data-testid={`revoke-key-${k.key_id}`}
              onClick={() => handleRevoke(k.key_id)}
              style={{
                background: "transparent", color: "#F87171",
                border: `1px solid #7F1D1D`, padding: "6px 12px",
                borderRadius: 6, fontSize: 11.5, fontWeight: 500, cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Revoke
            </button>
          </div>
        ))}
      </Section>

      <Section title="Documentation" description="Get started integrating Inference into your stack.">
        <div style={{ display: "flex", flexDirection: "column" }}>
          <a
            data-testid="link-salesforce-quickstart"
            href="/docs/salesforce"
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex", alignItems: "center", gap: 14, padding: "16px 18px",
              borderBottom: `1px solid ${BORDER}`, textDecoration: "none",
              color: "inherit", transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#0E121C")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#00A1E018", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#00A1E0" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12h6l3-9 6 18 3-9h3" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 2 }}>Salesforce Quickstart</div>
              <div style={{ fontSize: 11.5, color: TEXT_DIM }}>Step-by-step Apex setup, field mappings, troubleshooting.</div>
            </div>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={2}>
              <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <a
            data-testid="link-api-reference"
            href="/api/docs"
            target="_blank"
            rel="noreferrer"
            style={{
              display: "flex", alignItems: "center", gap: 14, padding: "16px 18px",
              textDecoration: "none", color: "inherit", transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#0E121C")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div style={{ width: 36, height: 36, borderRadius: 8, background: `${ACCENT}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 2 }}>API Reference</div>
              <div style={{ fontSize: 11.5, color: TEXT_DIM }}>OpenAPI spec for POST /api/v1/analyze — payloads, responses, error codes.</div>
            </div>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={2}>
              <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </Section>
    </div>
  );
};

const MonitoringSection = () => {
  const [cadence, setCadence] = useState("");
  const [allowed, setAllowed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    monitorAPI.getCadence()
      .then(res => {
        setCadence(res.data?.cadence || "weekly");
        setAllowed(res.data?.allowed || ["weekly"]);
      })
      .catch(() => setAllowed(["weekly"]))
      .finally(() => setLoading(false));
  }, []);

  // Persisted AND applied to every monitor in the workspace, so changing it
  // genuinely changes scan frequency rather than just the radio button.
  const choose = async (id) => {
    if (saving || id === cadence) return;
    if (!allowed.includes(id)) {
      toast.error("Not available on your plan", {
        description: "Upgrade to unlock this scan frequency.",
      });
      return;
    }
    const prev = cadence;
    setCadence(id);
    setSaving(true);
    try {
      const res = await monitorAPI.setCadence(id);
      toast.success("Monitoring cadence saved", { description: res.data?.message });
    } catch (err) {
      setCadence(prev);
      toast.error(err?.response?.data?.detail || "Could not save cadence");
    }
    setSaving(false);
  };

  if (loading) return <p style={{ color: TEXT_DIM, fontSize: 13 }}>Loading monitoring cadence...</p>;

  return (
    <Section title="Monitoring Cadence" description="Controls how often monitored companies in this workspace are scanned. Higher frequencies need a higher plan.">
      {[
        { id: "realtime", label: "Real-time", desc: "Scan within hours of detected changes", tier: "Org" },
        { id: "daily", label: "Daily", desc: "Scan once per day", tier: "Team" },
        { id: "weekly", label: "Weekly", desc: "Scan once per week", tier: "Individual" },
      ].map((c, i, arr) => (
        <button key={c.id} data-testid={`cadence-${c.id}`} onClick={() => choose(c.id)} style={{
          display: "flex", alignItems: "center", gap: 14, padding: "14px 18px",
          borderBottom: i < arr.length - 1 ? `1px solid ${BORDER}` : "none",
          background: cadence === c.id ? `${ACCENT}08` : "transparent",
          border: "none", cursor: saving ? "wait" : allowed.includes(c.id) ? "pointer" : "not-allowed",
          width: "100%", textAlign: "left", transition: "background 0.15s",
          opacity: allowed.includes(c.id) ? 1 : 0.45,
        }}>
          <div style={{
            width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
            background: cadence === c.id ? ACCENT : SURFACE_2,
            border: `2px solid ${cadence === c.id ? ACCENT : BORDER}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {cadence === c.id && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 2 }}>{c.label}</div>
            <div style={{ fontSize: 11, color: TEXT_DIM }}>{c.desc}</div>
          </div>
          <Badge
            label={allowed.includes(c.id) ? c.tier.toUpperCase() : `${c.tier.toUpperCase()} ONLY`}
            color={allowed.includes(c.id) ? (c.tier === "Org" ? "#8B5CF6" : c.tier === "Team" ? ACCENT : TEXT_DIM) : TEXT_DIM}
          />
        </button>
      ))}
    </Section>
  );
};

import { INDUSTRY_GROUPS, PERSONA_GROUPS, PLATFORM_GROUPS, PAIN_POINT_GROUPS, USE_CASE_GROUPS } from "./icp-options";
import { COUNTRY_GROUPS } from "./country-options";
import { MultiSelect, Dropdown } from "./icp-multiselect";
import { ICPPresetBar } from "./icp-presets";
const SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"];
const REVENUE_OPTIONS = ["Pre-revenue", "<$1M", "$1M-$5M", "$5M-$10M", "$10M-$50M", "$50M-$100M", "$100M-$500M", "$500M+"];
const AOV_OPTIONS_SETTINGS = ["<$10", "$10-$50", "$50-$100", "$100-$500", "$500-$1K", "$1K-$5K", "$5K-$50K", "$50K+", "Subscription", "Usage-based"];
const REGION_OPTIONS = ["North America", "Europe", "Asia Pacific", "Latin America", "Middle East", "Africa", "Global"];
const BIZ_MODEL_OPTIONS = ["B2B SaaS", "B2C SaaS", "Marketplace", "Platform", "API-first", "Freemium", "Enterprise", "Usage-based", "Hybrid"];

const ICPSection = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [sizeMin, setSizeMin] = useState("");
  const [sizeMax, setSizeMax] = useState("");
  const [revenue, setRevenue] = useState("");
  const [aov, setAov] = useState("");
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [bizModels, setBizModels] = useState([]);
  const [techStack, setTechStack] = useState([]);
  const [personaTitles, setPersonaTitles] = useState([]);
  const [targetPlatforms, setTargetPlatforms] = useState([]);
  const [painPoints, setPainPoints] = useState([]);
  const [useCases, setUseCases] = useState([]);

  const loadProfile = async () => {
    try {
      const res = await icpAPI.get();
      const d = res.data;
      if (d) {
        setIndustries(d.target_industries || []);
        setSizeMin(d.company_size_min || "");
        setSizeMax(d.company_size_max || "");
        setRevenue(d.revenue_range || "");
        setAov(d.average_order_value || "");
        setRegions(d.target_regions || []);
        setCountries(d.target_countries || []);
        setBizModels(d.business_models || []);
        setTechStack(d.tech_stack || []);
        setPersonaTitles((d.target_personas || []).map(p => (typeof p === "string" ? p : p?.title)).filter(Boolean));
        setTargetPlatforms(d.target_platforms || []);
        setPainPoints(d.pain_points || []);
        setUseCases(d.use_cases || []);
      }
    } catch (e) { /* keep whatever is on screen */ }
    setLoading(false);
  };

  useEffect(() => { loadProfile(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await icpAPI.update({
        target_industries: industries, company_size_min: sizeMin || null, company_size_max: sizeMax || null,
        revenue_range: revenue || null, average_order_value: aov || null,
        target_regions: regions, target_countries: countries, business_models: bizModels,
        tech_stack: techStack, target_personas: personaTitles.map(t => ({ title: t, department: "" })),
        target_platforms: targetPlatforms, pain_points: painPoints, use_cases: useCases,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error("Failed to save ICP:", err); }
    setSaving(false);
  };

  const labelStyle = { fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", marginBottom: 6, display: "block" };

  if (loading) return <p style={{ color: TEXT_DIM, fontSize: 13 }}>Loading ICP profile...</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <ICPPresetBar onSwitch={loadProfile} />

      <Section title="Target Industries" desc="Search and select the industries you sell to. Type to filter, or add your own.">
        <MultiSelect testId="icp-industries" groups={INDUSTRY_GROUPS} value={industries} onChange={setIndustries}
          placeholder="Search industries…" allowCustom />
      </Section>

      <Section title="Company Profile" desc="Define ideal company size, revenue, order value, regions and countries.">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>SIZE (MIN)</label>
            <Dropdown testId="icp-size-min" value={sizeMin} onChange={setSizeMin} options={SIZE_OPTIONS} />
          </div>
          <div>
            <label style={labelStyle}>SIZE (MAX)</label>
            <Dropdown testId="icp-size-max" value={sizeMax} onChange={setSizeMax} options={SIZE_OPTIONS} />
          </div>
        </div>
        <label style={labelStyle}>REVENUE RANGE</label>
        <div style={{ marginBottom: 14 }}>
          <Dropdown testId="icp-revenue" value={revenue} onChange={setRevenue} options={REVENUE_OPTIONS} />
        </div>
        <label style={labelStyle}>TARGET AVERAGE ORDER VALUE</label>
        <div style={{ marginBottom: 14 }}>
          <Dropdown testId="icp-aov" value={aov} onChange={setAov} options={AOV_OPTIONS_SETTINGS} />
        </div>
        <label style={labelStyle}>REGIONS</label>
        <div style={{ marginBottom: 14 }}>
          <MultiSelect testId="icp-regions" options={REGION_OPTIONS} value={regions} onChange={setRegions}
            placeholder="Search regions…" />
        </div>
        <label style={labelStyle}>COUNTRIES (OPTIONAL)</label>
        <MultiSelect testId="icp-countries" groups={COUNTRY_GROUPS} value={countries} onChange={setCountries}
          placeholder="Search countries…" groupSelectAll allowCustom />
        <p style={{ fontSize: 11, color: TEXT_DIM, marginTop: 6 }}>
          Countries take priority over regions in geography scoring when set. Leave empty to score by region only.
        </p>
      </Section>

      <Section title="Business Model & Tech Stack" desc="Define ideal business models and technologies.">
        <label style={labelStyle}>BUSINESS MODELS</label>
        <div style={{ marginBottom: 14 }}>
          <MultiSelect testId="icp-biz-models" options={BIZ_MODEL_OPTIONS} value={bizModels} onChange={setBizModels}
            placeholder="Search business models…" allowCustom />
        </div>
        <label style={labelStyle}>TECH STACK</label>
        <MultiSelect testId="icp-tech-stack" groups={PLATFORM_GROUPS} value={techStack} onChange={setTechStack}
          placeholder="e.g. Stripe, AWS, Shopify…" allowCustom />
      </Section>

      <Section title="Target Personas" desc="Who are you selling to?">
        <MultiSelect testId="icp-personas" groups={PERSONA_GROUPS} value={personaTitles} onChange={setPersonaTitles}
          placeholder="Search personas…" allowCustom />
      </Section>

      <Section title="Target Platforms" desc="Which tech platforms do your best-fit customers run on? Inference will match your selections against detected platforms on every analysed company.">
        <div data-testid="target-platforms-description" style={{ marginBottom: 16, fontSize: 12, color: TEXT_MUTED, lineHeight: 1.55, paddingLeft: 10, borderLeft: `2px solid ${BORDER}` }}>
          Example: if you sell a Shopify app, select <strong style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>Shopify</strong> and <strong style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>Shopify Plus</strong> — companies running them will score higher on the Tech Stack ICP dimension and surface platform-match signals.
        </div>
        <MultiSelect testId="icp-platforms" groups={PLATFORM_GROUPS} value={targetPlatforms} onChange={setTargetPlatforms}
          placeholder="Search platforms…" groupSelectAll />
      </Section>

      <Section title="Pain Points & Use Cases" desc="What problems does your product solve?">
        <div data-testid="pain-usecase-description" style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12.5, color: TEXT_MUTED, lineHeight: 1.6 }}>
            <strong style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>Pain Points</strong> — the specific problems your product solves.
            <br />
            <strong style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>Use Cases</strong> — how your product is used, and in which scenarios.
          </div>
          <div style={{ fontSize: 11.5, color: TEXT_MUTED, lineHeight: 1.55 }}>
            This section is <strong style={{ color: TEXT_PRIMARY, fontWeight: 600 }}>optional</strong>. PE analysts, Corp Dev, and Strategy teams can leave it empty.
          </div>
        </div>
        <label style={labelStyle}>PAIN POINTS</label>
        <div style={{ marginBottom: 14 }}>
          <MultiSelect testId="icp-pain-points" groups={PAIN_POINT_GROUPS} value={painPoints} onChange={setPainPoints}
            placeholder="e.g. High checkout abandonment…" allowCustom chipColor="#EF4444" />
        </div>
        <label style={labelStyle}>USE CASES</label>
        <MultiSelect testId="icp-use-cases" groups={USE_CASE_GROUPS} value={useCases} onChange={setUseCases}
          placeholder="e.g. Payment optimization…" allowCustom chipColor="#22C55E" />
      </Section>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
        {saved && <span style={{ fontSize: 12, color: "#22C55E", alignSelf: "center" }}>Saved successfully</span>}
        <button data-testid="icp-save-btn" onClick={handleSave} disabled={saving} style={{
          background: ACCENT, color: "#fff", border: "none", borderRadius: 8,
          padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: saving ? "wait" : "pointer",
          fontFamily: "'DM Sans', sans-serif", opacity: saving ? 0.7 : 1,
        }}>{saving ? "Saving..." : "Save ICP Profile"}</button>
      </div>
    </div>
  );
};

/* ── ROOT ── */
export default function InferenceSettings({ user: authUser }) {
  const [active, setActive] = useState(() => {
    try {
      const deepLink = sessionStorage.getItem("inference_settings_initial_section");
      if (deepLink) {
        sessionStorage.removeItem("inference_settings_initial_section");
        return deepLink;
      }
    } catch (e) { /* ignore */ }
    return "profile";
  });

  const panels = {
    profile: <ProfileSection authUser={authUser} />,
    icp: <ICPSection />,
    workspace: <WorkspaceSection />,
    members: <MembersSection />,
    billing: <BillingSection />,
    notifications: <NotificationsSection />,
    security: <SecuritySection />,
    "api-keys": <APIKeysSection onUpgrade={() => setActive("billing")} />,
    monitoring: <MonitoringSection />,
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input::placeholder { color: #374151; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        select, select option { background: #0D1117; color: #F9FAFB; }
        button { outline: none; }
      `}</style>

      <div style={{ display: "flex", flex: 1, maxWidth: 1100, margin: "0 auto", width: "100%", padding: "0 28px" }}>
        {/* Sidebar nav */}
        <aside style={{ width: 200, paddingTop: 28, paddingRight: 24, flexShrink: 0 }}>
          <nav style={{ display: "flex", flexDirection: "column", gap: 2, position: "sticky", top: 80 }}>
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => setActive(s.id)} style={{
                display: "flex", alignItems: "center", gap: 9, padding: "9px 12px",
                borderRadius: 8, border: "none", cursor: "pointer",
                background: active === s.id ? `${ACCENT}18` : "transparent",
                color: active === s.id ? ACCENT : TEXT_MUTED,
                fontSize: 13, fontWeight: active === s.id ? 600 : 400,
                fontFamily: "'DM Sans', sans-serif", textAlign: "left",
                transition: "all 0.15s",
              }}
                onMouseEnter={e => { if (active !== s.id) e.currentTarget.style.background = "#161B27"; }}
                onMouseLeave={e => { if (active !== s.id) e.currentTarget.style.background = "transparent"; }}
              >
                {active === s.id && <div style={{ position: "absolute", left: 28, width: 2, height: 22, background: ACCENT, borderRadius: "0 2px 2px 0" }} />}
                <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active === s.id ? 2 : 1.6} strokeLinecap="round" strokeLinejoin="round">
                  <path d={s.icon} />
                </svg>
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main style={{ flex: 1, paddingTop: 28, paddingBottom: 48 }}>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.02em", fontFamily: "'DM Sans', sans-serif" }}>
              {SECTIONS.find(s => s.id === active)?.label}
            </h2>
          </div>
          {panels[active]}
        </main>
      </div>
    </div>
  );
}
